import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Sparkles, Play, Pause, RefreshCw, Trash2, Loader2, Server, Cpu,
  Image as ImageIcon, Package, Zap, AlertTriangle, Eye, EyeOff, Wand2,
} from 'lucide-react';
import { api, compact, timeAgo } from '../../lib/api';
import { SkeletonList, SectionHead, Sheet, Empty } from '../../components/ui';
import { themeOf, rarityOf, SLOT_THEME } from '../../lib/classTheme';

const TABS = [
  { k: 'overview', l: '📊 Overview' },
  { k: 'heroes', l: '👤 Heroes' },
  { k: 'items', l: '🎒 Items' },
  { k: 'queue', l: '⚙️ Queue' },
];

export default function AdminGeneration() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('overview');
  const [genOpen, setGenOpen] = useState(null);
  const [preview, setPreview] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['genOverview'],
    queryFn: () => api.get('/generation/overview').then(r => r.data),
    refetchInterval: 10000,
  });

  const genHeroes = useMutation({
    mutationFn: (body) => api.post('/generation/heroes', body),
    onSuccess: ({ data: d }) => {
      toast.success(`✅ ${d.queued} hero jobs queued · ~${d.estimate.minutes}min · $${d.estimate.costUsd}`,
        { duration: 6000 });
      setGenOpen(null); qc.invalidateQueries(['genOverview']);
    },
    onError: e => toast.error(e.message),
  });

  const genItems = useMutation({
    mutationFn: (body) => api.post('/generation/items', body),
    onSuccess: ({ data: d }) => {
      toast.success(`✅ ${d.queued} item jobs queued · ~${d.estimate.minutes}min · $${d.estimate.costUsd}`,
        { duration: 6000 });
      setGenOpen(null); qc.invalidateQueries(['genOverview']);
    },
    onError: e => toast.error(e.message),
  });

  if (isLoading || !data) return <SkeletonList n={4} h="h-28" />;

  const { pool, redis, fal, batches, recent } = data;
  const heroesReady = pool.totals.heroes_ready;
  const heroesTotal = pool.totals.heroes_total;
  const itemsReady = pool.totals.items_ready;
  const itemsTotal = pool.totals.items_total;

  return (
    <div className="space-y-4">
      {/* ═══ HEALTH ═══ */}
      <div className="grid grid-cols-2 gap-2">
        <HealthCard icon={Server} label="Redis" ok={redis.ok}
          detail={redis.ok ? `${redis.latencyMs}ms · ${redis.memory || '?'}` : redis.error} />
        <HealthCard icon={Cpu} label="fal.ai" ok={fal.ok}
          detail={fal.ok ? `${fal.t2i} [${fal.mode}]` : fal.error} />
      </div>

      {/* ═══ POOL PROGRESS ═══ */}
      <div className="card p-4">
        <SectionHead title="Generation pool"
          sub={`$${Number(pool.totals.cost_usd || 0).toFixed(2)} spent`}
          right={
            <div className="flex gap-1.5">
              <button onClick={() => setGenOpen('heroes')} className="btn-primary btn-sm">
                <Sparkles size={12} /> Heroes
              </button>
              <button onClick={() => setGenOpen('items')} className="btn-grape btn-sm">
                <Package size={12} /> Items
              </button>
            </div>
          } />

        <Progress label="👤 Hero portraits" ready={heroesReady} total={heroesTotal} color="#f97316" />
        <div className="h-2" />
        <Progress label="🎒 Item icons" ready={itemsReady} total={itemsTotal} color="#a855f7" />
      </div>

      {/* ═══ TABS ═══ */}
      <div className="flex gap-1 bg-white p-1 rounded-xl border border-ink-100 overflow-x-auto no-scrollbar">
        {TABS.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`flex-1 min-w-[88px] py-2 rounded-lg text-xs font-bold transition
              ${tab === t.k ? 'bg-ink-900 text-white' : 'text-ink-500'}`}>{t.l}</button>
        ))}
      </div>

      {/* ═══ OVERVIEW ═══ */}
      {tab === 'overview' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {Object.entries(pool.byClass).map(([k, c]) => {
              const t = themeOf(k);
              const hp = c.heroes.total ? (c.heroes.ready / c.heroes.total) * 100 : 0;
              const ip = c.items.total ? (c.items.ready / c.items.total) * 100 : 0;
              return (
                <div key={k} className="card p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">{t.icon}</span>
                    <span className="font-black text-sm capitalize">{k}</span>
                  </div>
                  <MiniBar label="Heroes" v={`${c.heroes.ready}/${c.heroes.total}`} pct={hp} color={t.hex} />
                  <div className="h-1.5" />
                  <MiniBar label="Items" v={`${c.items.ready}/${c.items.total}`} pct={ip} color="#a855f7" />
                  {(c.heroes.failed > 0 || c.items.failed > 0) && (
                    <p className="text-[10px] font-bold text-red-500 mt-1.5">
                      ⚠️ {c.heroes.failed + c.items.failed} failed
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {recent?.length > 0 && (
            <div>
              <SectionHead title="Recently generated" />
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {recent.slice(0, 18).map((r, i) => (
                  <button key={i} onClick={() => setPreview(r)}
                    className="relative rounded-xl overflow-hidden border-2 border-ink-100
                      hover:border-brand-300 transition">
                    <img src={r.image_url} alt=""
                      className={`w-full object-cover ${r.type === 'hero' ? 'aspect-[9/16]' : 'aspect-square'}`}
                      loading="lazy" />
                    <div className="absolute inset-x-0 bottom-0 px-1 py-0.5
                      bg-gradient-to-t from-black/80 to-transparent">
                      <p className="text-[7px] font-black text-white truncate">
                        {r.type === 'hero' ? `${r.class_key} #${r.idx}` : `${r.slot}/${r.rarity}`}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {batches?.length > 0 && (
            <div>
              <SectionHead title="Recent batches" />
              <div className="card p-2 space-y-1">
                {batches.slice(0, 8).map(b => (
                  <div key={b.id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-ink-50">
                    <div className={`w-9 h-9 rounded-xl grid place-items-center text-white shrink-0
                      ${b.status === 'done' ? 'bg-mint-500'
                        : b.status === 'running' ? 'bg-brand-500'
                        : b.status === 'partial' ? 'bg-amber-500' : 'bg-ink-400'}`}>
                      {b.type === 'heroes' ? '👤' : '🎒'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-xs truncate">{b.label}</p>
                      <p className="text-[10px] text-ink-400 font-semibold">
                        {b.code} · {timeAgo(b.created_at)}
                        {b.created_by_name && ` · @${b.created_by_name}`}
                      </p>
                      <div className="h-1 rounded-full bg-ink-100 mt-1 overflow-hidden">
                        <div className={`h-full rounded-full ${b.failed > 0 ? 'bg-amber-500' : 'bg-mint-500'}`}
                          style={{ width: `${b.progress}%` }} />
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-black text-xs">{b.completed}/{b.total}</p>
                      {b.failed > 0 && <p className="text-[10px] text-red-500 font-bold">{b.failed} ✗</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'heroes' && <HeroTemplates onPreview={setPreview} />}
      {tab === 'items' && <ItemImages onPreview={setPreview} />}
      {tab === 'queue' && <QueueMonitor />}

      {/* ═══ GENERATE SHEETS ═══ */}
      <GenerateHeroesSheet open={genOpen === 'heroes'} onClose={() => setGenOpen(null)}
        pool={pool} onSubmit={(b) => genHeroes.mutate(b)} busy={genHeroes.isPending} />
      <GenerateItemsSheet open={genOpen === 'items'} onClose={() => setGenOpen(null)}
        pool={pool} onSubmit={(b) => genItems.mutate(b)} busy={genItems.isPending} />

      {/* ═══ PREVIEW ═══ */}
      <Sheet open={!!preview} onClose={() => setPreview(null)}
        title={preview?.name || `${preview?.class_key} ${preview?.slot || ''}`}>
        {preview && (
          <div className="space-y-3">
            <img src={preview.image_url} alt=""
              className={`w-full rounded-2xl ${preview.type === 'hero' ? 'aspect-[9/16]' : 'aspect-square'} object-cover`} />
            <div className="grid grid-cols-2 gap-2 text-center">
              <Meta l="Type" v={preview.type} />
              <Meta l="Class" v={preview.class_key} />
              {preview.slot && <Meta l="Slot" v={preview.slot} />}
              {preview.rarity && <Meta l="Rarity" v={preview.rarity} />}
              <Meta l="Cost" v={`$${Number(preview.cost_usd || 0).toFixed(3)}`} />
              <Meta l="Generated" v={timeAgo(preview.generated_at)} />
            </div>
            <a href={preview.image_url} target="_blank" rel="noreferrer" className="btn-ghost w-full">
              Open full size
            </a>
          </div>
        )}
      </Sheet>
    </div>
  );
}

/* ============================================================
   SUB-COMPONENTS
   ============================================================ */
function HealthCard({ icon: Icon, label, ok, detail }) {
  return (
    <div className={`card p-3 ${ok ? '' : 'ring-2 ring-red-300'}`}>
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg grid place-items-center text-white
          ${ok ? 'bg-mint-500' : 'bg-red-500'}`}>
          <Icon size={15} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-xs">{label}</p>
          <p className="text-[10px] text-ink-400 font-semibold truncate">{detail}</p>
        </div>
      </div>
    </div>
  );
}

function Progress({ label, ready, total, color }) {
  const pct = total ? Math.round((ready / total) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-xs font-bold mb-1">
        <span>{label}</span>
        <span style={{ color }}>{ready}/{total} · {pct}%</span>
      </div>
      <div className="h-2.5 rounded-full bg-ink-100 overflow-hidden">
        <motion.div className="h-full rounded-full" style={{ background: color }}
          animate={{ width: `${pct}%` }} transition={{ duration: 0.6 }} />
      </div>
    </div>
  );
}

function MiniBar({ label, v, pct, color }) {
  return (
    <div>
      <div className="flex justify-between text-[10px] font-bold mb-0.5">
        <span className="text-ink-400">{label}</span>
        <span>{v}</span>
      </div>
      <div className="h-1.5 rounded-full bg-ink-100 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function Meta({ l, v }) {
  return (
    <div className="bg-ink-50 rounded-xl p-2">
      <p className="text-[9px] font-black text-ink-400 uppercase">{l}</p>
      <p className="font-bold text-sm capitalize">{v}</p>
    </div>
  );
}

/* ---------------- HERO TEMPLATES ---------------- */
function HeroTemplates({ onPreview }) {
  const qc = useQueryClient();
  const [cls, setCls] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['genTemplates', cls],
    queryFn: () => api.get('/generation/templates', { params: { class_key: cls || undefined } })
      .then(r => r.data),
  });

  const toggle = useMutation({
    mutationFn: ({ id, active }) => api.patch(`/generation/templates/${id}`, { active }),
    onSuccess: () => { toast.success('Updated'); qc.invalidateQueries(['genTemplates']); },
  });

  const regen = useMutation({
    mutationFn: (id) => api.post(`/generation/templates/${id}/regenerate`),
    onSuccess: () => { toast.success('Re-queued'); qc.invalidateQueries(['genTemplates', 'genOverview']); },
    onError: e => toast.error(e.message),
  });

  if (isLoading) return <SkeletonList n={6} h="h-24" />;

  return (
    <div className="space-y-3">
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
        <FilterChip active={!cls} onClick={() => setCls('')} label="All" />
        {Object.keys(themeOf('warrior') && { warrior: 1, mage: 1, rogue: 1, ranger: 1, guardian: 1, bard: 1 })
          .map(k => (
            <FilterChip key={k} active={cls === k} onClick={() => setCls(k)}
              label={`${themeOf(k).icon} ${k}`} />
          ))}
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {data?.map(t => {
          const th = themeOf(t.class_key);
          return (
            <div key={t.id} className={`card overflow-hidden ${!t.active ? 'opacity-50' : ''}`}>
              <button onClick={() => onPreview({ ...t, type: 'hero', idx: t.variant_index })}
                className="relative block w-full aspect-[9/16] bg-ink-100">
                {t.image_url
                  ? <img src={t.thumb_url || t.image_url} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full grid place-items-center text-ink-300">
                      {t.status === 'generating'
                        ? <Loader2 size={20} className="animate-spin" />
                        : t.status === 'failed' ? '❌' : '⏳'}
                    </div>}
                <span className="absolute top-1 left-1 px-1 rounded text-[8px] font-black text-white"
                  style={{ background: th.hex }}>#{t.variant_index}</span>
                {t.times_chosen > 0 && (
                  <span className="absolute top-1 right-1 px-1 rounded bg-black/60 text-white text-[8px] font-black">
                    {t.times_chosen}
                  </span>
                )}
              </button>

              <div className="p-1.5">
                <p className="text-[9px] font-black truncate">{t.name}</p>
                <div className="flex gap-1 mt-1">
                  <button onClick={() => toggle.mutate({ id: t.id, active: !t.active })}
                    className="flex-1 py-1 rounded bg-ink-100 text-[8px] font-black">
                    {t.active ? <Eye size={9} className="mx-auto" /> : <EyeOff size={9} className="mx-auto" />}
                  </button>
                  <button onClick={() => confirm('Regenerate this hero?') && regen.mutate(t.id)}
                    className="flex-1 py-1 rounded bg-grape-100 text-grape-600 text-[8px] font-black">
                    <RefreshCw size={9} className="mx-auto" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- ITEM IMAGES ---------------- */
function ItemImages({ onPreview }) {
  const qc = useQueryClient();
  const [cls, setCls] = useState('warrior');
  const [slot, setSlot] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['genItemImages', cls, slot],
    queryFn: () => api.get('/generation/item-images', {
      params: { class_key: cls, slot: slot || undefined },
    }).then(r => r.data),
  });

  const regen = useMutation({
    mutationFn: (id) => api.post(`/generation/item-images/${id}/regenerate`),
    onSuccess: () => { toast.success('Re-queued'); qc.invalidateQueries(['genItemImages', 'genOverview']); },
    onError: e => toast.error(e.message),
  });

  if (isLoading) return <SkeletonList n={6} h="h-24" />;

  return (
    <div className="space-y-3">
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
        {['warrior', 'mage', 'rogue', 'ranger', 'guardian', 'bard'].map(k => (
          <FilterChip key={k} active={cls === k} onClick={() => setCls(k)}
            label={`${themeOf(k).icon} ${k}`} />
        ))}
      </div>
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
        <FilterChip active={!slot} onClick={() => setSlot('')} label="All slots" />
        {Object.entries(SLOT_THEME).map(([k, s]) => (
          <FilterChip key={k} active={slot === k} onClick={() => setSlot(k)}
            label={`${s.icon} ${s.label}`} />
        ))}
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {data?.map(ci => {
          const r = rarityOf(ci.rarity);
          return (
            <div key={ci.id} className="card overflow-hidden">
              <button onClick={() => onPreview({ ...ci, type: 'item' })}
                className="relative block w-full aspect-square bg-ink-100">
                {ci.image_url
                  ? <img src={ci.thumb_url || ci.image_url} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full grid place-items-center text-ink-300">
                      {ci.status === 'generating'
                        ? <Loader2 size={18} className="animate-spin" />
                        : ci.status === 'failed' ? '❌' : '⏳'}
                    </div>}
                <span className="absolute inset-x-0 bottom-0 px-1 py-0.5 text-[7px] font-black text-white"
                  style={{ background: `${r.hex}dd` }}>{r.label}</span>
              </button>
              <div className="p-1.5">
                <p className="text-[9px] font-black">{SLOT_THEME[ci.slot]?.icon} {ci.slot}</p>
                <button onClick={() => regen.mutate(ci.id)}
                  className="w-full mt-1 py-1 rounded bg-grape-100 text-grape-600 text-[8px] font-black">
                  <RefreshCw size={9} className="mx-auto" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- QUEUE MONITOR ---------------- */
function QueueMonitor() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['genQueues'],
    queryFn: () => api.get('/generation/queues').then(r => r.data),
    refetchInterval: 4000,
  });

  const act = useMutation({
    mutationFn: ({ name, action, body }) => api.post(`/generation/queues/${name}/${action}`, body || {}),
    onSuccess: ({ data: d }) => {
      toast.success(d.retried != null ? `${d.retried} retried`
        : d.cleared != null ? `${d.cleared} cleared` : 'Done');
      qc.invalidateQueries(['genQueues', 'genOverview']);
    },
    onError: e => toast.error(e.message),
  });

  if (isLoading) return <SkeletonList n={3} h="h-28" />;

  return (
    <div className="space-y-3">
      {data?.map(q => (
        <div key={q.name} className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-black text-sm">{q.label}</p>
              <p className="text-[11px] text-ink-400 font-semibold">
                {q.paused ? '⏸️ Paused' : '▶️ Running'}
              </p>
            </div>
            <div className="flex gap-1.5">
              <button onClick={() => act.mutate({ name: q.name, action: 'pause', body: { paused: !q.paused } })}
                className="btn-ghost btn-sm">
                {q.paused ? <Play size={12} /> : <Pause size={12} />}
              </button>
              <button onClick={() => act.mutate({ name: q.name, action: 'retry' })}
                disabled={!q.failed} className="btn-ghost btn-sm">
                <RefreshCw size={12} />
              </button>
              <button onClick={() => confirm(`Clear ${q.label} queue?`)
                && act.mutate({ name: q.name, action: 'clear' })}
                className="btn btn-sm bg-red-50 text-red-600">
                <Trash2 size={12} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-5 gap-1.5 text-center">
            <QStat l="Waiting" v={q.waiting} c="bg-ink-100 text-ink-600" />
            <QStat l="Active" v={q.active} c="bg-brand-100 text-brand-700" />
            <QStat l="Done" v={q.completed} c="bg-mint-400/20 text-mint-700" />
            <QStat l="Failed" v={q.failed} c="bg-red-100 text-red-600" />
            <QStat l="Delayed" v={q.delayed} c="bg-amber-100 text-amber-700" />
          </div>
        </div>
      ))}
    </div>
  );
}

function QStat({ l, v, c }) {
  return (
    <div className={`rounded-xl p-2 ${c}`}>
      <p className="font-black text-base">{v}</p>
      <p className="text-[8px] font-bold uppercase">{l}</p>
    </div>
  );
}

function FilterChip({ active, onClick, label }) {
  return (
    <button onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap capitalize transition
        ${active ? 'bg-ink-900 text-white' : 'bg-white border border-ink-200 text-ink-500'}`}>
      {label}
    </button>
  );
}

/* ---------------- GENERATE SHEETS ---------------- */
function GenerateHeroesSheet({ open, onClose, pool, onSubmit, busy }) {
  const [classes, setClasses] = useState([]);
  const [count, setCount] = useState(5);
  const [force, setForce] = useState(false);

  const all = Object.keys(pool?.byClass || {});
  const target = classes.length || all.length;
  const jobs = target * count;

  return (
    <Sheet open={open} onClose={onClose} title="Generate hero portraits">
      <div className="space-y-4">
        <div>
          <label className="label">Classes (empty = all)</label>
          <div className="grid grid-cols-3 gap-2">
            {all.map(k => {
              const t = themeOf(k);
              const on = classes.includes(k);
              return (
                <button key={k}
                  onClick={() => setClasses(on ? classes.filter(x => x !== k) : [...classes, k])}
                  className={`p-2 rounded-xl border-2 text-center transition
                    ${on ? 'text-white' : 'bg-white border-ink-200'}`}
                  style={on ? { background: t.hex, borderColor: t.hex } : {}}>
                  <div className="text-lg">{t.icon}</div>
                  <p className="text-[9px] font-black capitalize">{k}</p>
                  <p className="text-[8px] opacity-70">{pool.byClass[k].heroes.ready} ready</p>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="label">Variants per class: {count}</label>
          <input type="range" min="1" max="10" value={count}
            onChange={e => setCount(Number(e.target.value))}
            className="w-full accent-brand-500" />
        </div>

        <label className="flex items-center gap-2 text-sm font-bold">
          <input type="checkbox" checked={force} onChange={e => setForce(e.target.checked)}
            className="w-4 h-4 accent-brand-500" />
          Force regenerate existing heroes
        </label>

        <div className="p-3 rounded-xl bg-brand-50 border border-brand-200 space-y-1">
          <Row l="Jobs" v={jobs} />
          <Row l="Estimated time" v={`~${Math.ceil(jobs * 35 / 3 / 60)} min`} />
          <Row l="Estimated cost" v={`$${(jobs * 0.04).toFixed(2)}`} bold />
        </div>

        {force && (
          <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex gap-2">
            <AlertTriangle size={16} className="text-amber-600 shrink-0" />
            <p className="text-[11px] font-bold text-amber-800">
              Existing heroes will be regenerated. Users keep their template link but see the new artwork.
            </p>
          </div>
        )}

        <button onClick={() => onSubmit({ classes: classes.length ? classes : undefined, count, force })}
          disabled={busy} className="btn-primary w-full py-3">
          {busy ? <><Loader2 size={16} className="animate-spin" /> Queueing…</>
            : <><Sparkles size={16} /> Generate {jobs} heroes</>}
        </button>
      </div>
    </Sheet>
  );
}

function GenerateItemsSheet({ open, onClose, pool, onSubmit, busy }) {
  const [classes, setClasses] = useState([]);
  const [slots, setSlots] = useState([]);
  const [rarities, setRarities] = useState([]);
  const [force, setForce] = useState(false);

  const all = Object.keys(pool?.byClass || {});
  const allSlots = Object.keys(SLOT_THEME);
  const allRar = ['common', 'uncommon', 'rare', 'epic', 'legendary'];

  const jobs = (classes.length || all.length) *
    (slots.length || allSlots.length) * (rarities.length || allRar.length);

  const tog = (arr, set, v) => set(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);

  return (
    <Sheet open={open} onClose={onClose} title="Generate item icons" wide>
      <div className="space-y-4">
        <div>
          <label className="label">Classes (empty = all)</label>
          <div className="grid grid-cols-3 gap-2">
            {all.map(k => {
              const t = themeOf(k);
              const on = classes.includes(k);
              return (
                <button key={k} onClick={() => tog(classes, setClasses, k)}
                  className={`p-2 rounded-xl border-2 text-center transition
                    ${on ? 'text-white' : 'bg-white border-ink-200'}`}
                  style={on ? { background: t.hex, borderColor: t.hex } : {}}>
                  <div className="text-lg">{t.icon}</div>
                  <p className="text-[9px] font-black capitalize">{k}</p>
                  <p className="text-[8px] opacity-70">{pool.byClass[k].items.ready}/25</p>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="label">Slots (empty = all)</label>
          <div className="flex gap-1.5 flex-wrap">
            {allSlots.map(k => (
              <button key={k} onClick={() => tog(slots, setSlots, k)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition
                  ${slots.includes(k) ? 'bg-ink-900 text-white' : 'bg-white border border-ink-200 text-ink-500'}`}>
                {SLOT_THEME[k].icon} {SLOT_THEME[k].label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="label">Rarities (empty = all)</label>
          <div className="flex gap-1.5 flex-wrap">
            {allRar.map(k => {
              const r = rarityOf(k);
              const on = rarities.includes(k);
              return (
                <button key={k} onClick={() => tog(rarities, setRarities, k)}
                  className="px-3 py-1.5 rounded-lg text-xs font-black border transition"
                  style={on
                    ? { background: r.hex, color: '#fff', borderColor: r.hex }
                    : { background: '#fff', color: r.hex, borderColor: `${r.hex}55` }}>
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm font-bold">
          <input type="checkbox" checked={force} onChange={e => setForce(e.target.checked)}
            className="w-4 h-4 accent-brand-500" />
          Force regenerate existing items
        </label>

        <div className="p-3 rounded-xl bg-grape-50 border border-grape-200 space-y-1">
          <Row l="Jobs" v={jobs} />
          <Row l="Estimated time" v={`~${Math.ceil(jobs * 30 / 5 / 60)} min`} />
          <Row l="Estimated cost" v={`$${(jobs * 0.04).toFixed(2)}`} bold />
        </div>

        <button onClick={() => onSubmit({
          classes: classes.length ? classes : undefined,
          slots: slots.length ? slots : undefined,
          rarities: rarities.length ? rarities : undefined,
          force,
        })} disabled={busy} className="btn-grape w-full py-3">
          {busy ? <><Loader2 size={16} className="animate-spin" /> Queueing…</>
            : <><Package size={16} /> Generate {jobs} items</>}
        </button>
      </div>
    </Sheet>
  );
}

function Row({ l, v, bold }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="font-semibold text-ink-600">{l}</span>
      <span className={bold ? 'font-black' : 'font-bold'}>{v}</span>
    </div>
  );
}